<script setup lang="ts">
import { reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { CaldavAccount, CaldavCalendar, SharePermissionType } from '@/api'
import UiGroup from '@/components/UiGroup.vue'
import UiList from '@/components/UiList.vue'
import UiRow from '@/components/UiRow.vue'
import { useAccountsEditor } from '@/composables/useAccountsEditor'
import { grantLabel } from '@/settings/grantLabel'

// Who may suggest a todo to this calendar: the grants (Remove) and the form for a new one. Adding grants for other people stays here;
// the overview of all of them is Settings > Sharing & blocked users.
const props = defineProps<{ account: CaldavAccount; calendar: CaldavCalendar }>()
const { t } = useI18n()
const editor = useAccountsEditor()

const form = reactive({ permission_type: 'specific_user' as SharePermissionType, target_username: '', target_chat_id: '' })
const submitting = ref(false)

async function submit(): Promise<void> {
  submitting.value = true
  try {
    const added = await editor.addGrant(props.calendar, {
      permission_type: form.permission_type,
      target_username: form.permission_type === 'specific_user' ? form.target_username : undefined,
      target_chat_id:
        form.permission_type === 'specific_chat' && form.target_chat_id ? Number(form.target_chat_id) : undefined,
    })
    if (added) {
      form.target_username = ''
      form.target_chat_id = ''
    }
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <div class="calendar-sharing">
    <p v-if="editor.grantsError[calendar.id]" class="error">{{ editor.grantsError[calendar.id] }}</p>
    <p v-else-if="editor.grantsLoading[calendar.id] && !editor.grantsByCalendar[calendar.id]">{{ t('common.loading') }}</p>
    <template v-else>
      <UiGroup>
        <template #footer>
          <p v-if="!(editor.grantsByCalendar[calendar.id] ?? []).length">{{ t('accounts.noGrants') }}</p>
        </template>
        <UiList v-if="(editor.grantsByCalendar[calendar.id] ?? []).length">
          <UiRow v-for="grant in editor.grantsByCalendar[calendar.id]" :key="grant.id" class="grant">
            <span class="title">{{ grantLabel(grant, t) }}</span>
            <button type="button" class="remove" @click="editor.removeGrant(props.calendar, grant)">
              {{ t('common.remove') }}
            </button>
          </UiRow>
        </UiList>
      </UiGroup>
    </template>

    <form @submit.prevent="submit">
      <UiGroup>
        <UiList>
          <UiRow as="label" :label="t('accounts.grant')">
            <select v-model="form.permission_type" class="ui-select" :aria-label="t('accounts.grant')">
              <option value="specific_user">{{ t('accounts.grantSpecificUser') }}</option>
              <option value="specific_chat">{{ t('accounts.grantSpecificChat') }}</option>
              <option value="any_group">{{ t('accounts.grantAnyGroup') }}</option>
              <option value="everyone">{{ t('accounts.grantEveryone') }}</option>
            </select>
          </UiRow>
          <UiRow v-if="form.permission_type === 'specific_user'" as="label" :label="t('common.username')">
            <input
              v-model="form.target_username"
              class="ui-input"
              required
              placeholder="@someone"
              autocapitalize="off"
              autocorrect="off"
              spellcheck="false"
              :aria-label="t('common.username')"
            />
          </UiRow>
          <UiRow v-if="form.permission_type === 'specific_chat'" as="label" :label="t('accounts.chatId')">
            <input
              v-model="form.target_chat_id"
              class="ui-input"
              required
              placeholder="e.g. -100123456789"
              :aria-label="t('accounts.chatId')"
            />
          </UiRow>
        </UiList>
        <template #footer>
          <p>{{ t('accounts.sharingHint') }}</p>
        </template>
      </UiGroup>
      <UiGroup>
        <UiList>
          <UiRow as="button" type="submit" class="action" :disabled="submitting" data-testid="add-grant">
            {{ submitting ? t('accounts.adding') : t('accounts.addGrant') }}
          </UiRow>
        </UiList>
      </UiGroup>
    </form>
  </div>
</template>

<style scoped>
.title {
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
}

.remove {
  flex: none;
  background: none;
  border: 0;
  color: var(--tg-destructive-text-color);
  font: inherit;
  cursor: pointer;
}

.action {
  width: 100%;
  border: 0;
  background: none;
  color: var(--tg-link-color);
  text-align: left;
  font: inherit;
  cursor: pointer;
}

.action:disabled {
  opacity: 0.6;
  cursor: default;
}

.error {
  color: var(--tg-destructive-text-color);
}
</style>
